import { useId } from "react";
import { Link } from "react-router-dom";
import { FiChevronRight } from "react-icons/fi";

/** Wrap a dashboard page so every `dx-` rule applies. */
export function DashboardPage({ children, className = "" }) {
  return <div className={`dx ${className}`.trim()}>{children}</div>;
}

export function SectionCard({ title, action, children, className = "", ...rest }) {
  const titleId = useId();
  return (
    <section
      className={`dx-card ${className}`.trim()}
      aria-labelledby={title && !rest["aria-label"] ? titleId : undefined}
      {...rest}
    >
      {title || action ? (
        <div className="dx-card-head">
          {title ? <h2 id={titleId}>{title}</h2> : <span />}
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function TextLink({ to, children }) {
  return (
    <Link to={to} className="dx-link">
      {children}
      <FiChevronRight aria-hidden="true" size={16} />
    </Link>
  );
}

/** A figure with a label. Pass `to` and the whole tile becomes a link. */
export function StatTile({ label, value, note, to }) {
  const body = (
    <>
      <span className="dx-tile-label">{label}</span>
      <span className="dx-tile-value">{value}</span>
      {note ? <span className="dx-tile-note">{note}</span> : null}
    </>
  );
  return to ? (
    <Link to={to} className="dx-tile">
      {body}
    </Link>
  ) : (
    <div className="dx-tile">{body}</div>
  );
}

/** One line in a list: icon, two lines of text, and a value or chevron on the right. */
export function ListRow({ icon: Icon, tone, title, subtitle, end, to }) {
  const body = (
    <>
      {Icon ? (
        <span className={`dx-row-icon${tone ? ` dx-row-icon--${tone}` : ""}`}>
          <Icon aria-hidden="true" size={18} />
        </span>
      ) : null}
      <span className="dx-row-main">
        <span className="dx-row-title">{title}</span>
        {subtitle ? <span className="dx-row-sub" style={{ display: "block" }}>{subtitle}</span> : null}
      </span>
      {end !== undefined && end !== null ? <span className="dx-row-end">{end}</span> : null}
      {to ? <FiChevronRight aria-hidden="true" size={18} style={{ color: "#62626a", flex: "none" }} /> : null}
    </>
  );
  return to ? (
    <Link to={to} className="dx-row">
      {body}
    </Link>
  ) : (
    <div className="dx-row">{body}</div>
  );
}

export function EmptyState({ icon: Icon, title, children, action }) {
  return (
    <div className="dx-empty" role="status">
      {Icon ? <Icon aria-hidden="true" size={30} style={{ opacity: 0.6 }} /> : null}
      <strong>{title}</strong>
      {children ? <span>{children}</span> : null}
      {action}
    </div>
  );
}

export function Pill({ tone = "ok", children }) {
  return <span className={`dx-pill dx-pill--${tone}`}>{children}</span>;
}

/** Icon buttons that jump to the things people do most. `data-tour` keeps onboarding anchors working. */
export function QuickActions({ actions, asCard = false }) {
  return (
    <div className={`dx-actions${asCard ? " dx-actions--card" : ""}`}>
      {actions.map(({ label, to, icon: Icon, tour }) => (
        <Link key={label} to={to} className="dx-action" data-tour={tour}>
          <span className="dx-action-icon">
            <Icon aria-hidden="true" size={22} />
          </span>
          <span>{label}</span>
        </Link>
      ))}
    </div>
  );
}

/** A count that asks for action. Links to the list where the work is done. */
export function AttentionTile({ icon: Icon, title, count, detail, tone = "warn", to }) {
  return (
    <Link to={to} className={`dx-attn dx-attn--${tone}`}>
      <span className="dx-attn-top">
        {Icon ? (
          <span className="dx-attn-icon">
            <Icon aria-hidden="true" size={18} />
          </span>
        ) : null}
        <FiChevronRight aria-hidden="true" size={18} className="dx-attn-go" />
      </span>
      <span className="dx-attn-count">{count}</span>
      <span className="dx-attn-title">{title}</span>
      {detail ? <span className="dx-attn-detail">{detail}</span> : null}
    </Link>
  );
}

/** Native select with a visible label, for filters such as period and day. */
export function SelectControl({ id, label, value, onChange, options }) {
  return (
    <span className="dx-select-wrap">
      <label htmlFor={id} className="dx-sr">
        {label}
      </label>
      <select id={id} className="dx-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </span>
  );
}

export function ProgressBar({ value, label }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div
      className="dx-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={label}
    >
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

export function SkeletonBlock({ height = 80, style }) {
  return <span className="dx-skel" style={{ height, ...style }} aria-hidden="true" />;
}
